import { useEffect, useMemo } from 'react'
import useSWR from 'swr'
import { ProgrammeModel } from '@/models/programme.model'
import { programmesService } from '@/services/programmesService'
import { useProgrammesStore } from '@/stores/programmes.store'

const PROGRAMMES_SWR_KEY = 'public-programmes'

const fetchProgrammes = async (): Promise<ProgrammeModel[]> => {
  return programmesService.getProgrammes({
    requireAuth: false,
    skipCSRF: true,
    perPage: 50,
  })
}

export interface UseProgrammesResult {
  programmes: ProgrammeModel[]
  isLoading: boolean
  isRefreshing: boolean
  hasError: boolean
  lastFetchedAt: number | null
  refetch: () => void
}

export function useProgrammes(): UseProgrammesResult {
  const cachedProgrammes = useProgrammesStore((state) => state.programmes)
  const lastFetchedAt = useProgrammesStore((state) => state.lastFetchedAt)
  const setProgrammes = useProgrammesStore((state) => state.setProgrammes)

  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<ProgrammeModel[], Error>(PROGRAMMES_SWR_KEY, fetchProgrammes, {
    revalidateOnFocus: true,
    dedupingInterval: 5 * 60 * 1000,
    fallbackData: cachedProgrammes.length > 0 ? cachedProgrammes : undefined,
  })

  useEffect(() => {
    if (data && data.length > 0) {
      setProgrammes(data)
    }
  }, [data, setProgrammes])

  const programmes = useMemo(
    () => data ?? cachedProgrammes,
    [cachedProgrammes, data],
  )

  const initialLoading = programmes.length === 0 && isLoading
  const refreshInProgress = programmes.length > 0 && isValidating

  return {
    programmes,
    isLoading: initialLoading,
    isRefreshing: refreshInProgress,
    hasError: Boolean(error),
    lastFetchedAt,
    refetch: () => {
      void mutate()
    },
  }
}
